import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Order, LogElement } from '@models/order';
import { OrderService } from '@services/order.service';
import { FormatData } from '@classes/format-data';
import { isPriorityRedoReason } from '../constants/redo-reasons';
import { clearCopiedSuperbill } from '@classes/superbill';

export class RedoJob {
  static redoInvoicePrefix(invoiceNumber: string): string {
    return FormatData.displayInvoiceNumber(invoiceNumber).replace(
      /-R(\d+)?$/i,
      ''
    );
  }

  static nextRInvoice(originalInvoice: string, orders: Order[]): string {
    const prefix = RedoJob.redoInvoicePrefix(originalInvoice);
    if (!prefix) {
      return '';
    }
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-R(\\d+)?$`, 'i');
    let max = 0;
    (orders || []).forEach((order) => {
      const invoice = FormatData.displayInvoiceNumber(order?.invoiceNumber);
      const match = invoice.match(pattern);
      if (!match) {
        return;
      }
      const n = match[1] ? parseInt(match[1], 10) : 1;
      if (!isNaN(n) && n > max) {
        max = n;
      }
    });
    if (max === 0) {
      return `${prefix}-R`;
    }
    return `${prefix}-R${max + 1}`;
  }

  static originalPaidAmount(order: any): number {
    const product = order?.products?.[0] || {};
    const subtotal = Number(product.subtotal);
    if (!isNaN(subtotal) && subtotal > 0) {
      return subtotal;
    }
    const retail = Number(product.retail) || 0;
    const custom = Number(product.custom) || 0;
    const lensPrice = Number(product.lens?.price) || 0;
    return retail + custom + lensPrice;
  }

  static buildLinkedRedoOrder(
    original: any,
    originalInvoice: string,
    invoiceNumber: string,
    reason: string,
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    }
  ): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clearCopiedSuperbill(clone);
    const paid = RedoJob.originalPaidAmount(original);
    const priority = isPriorityRedoReason(reason);
    clone._id = '';
    clone.invoiceNumber = invoiceNumber;
    clone.linkedInvoice = originalInvoice;
    clone.redoReason = reason;
    clone.redoPriority = priority;
    clone.redoPaidCredit = paid;
    clone.frameOnly = false;
    clone.frameOnlyTaxCredit = 0;
    clone.exchangeCredit = 0;
    clone.status = '';
    clone.todo = 'SAVE';
    clone.jobAction = '';
    clone.jobActions = [];
    clone.lensStatus = '';
    clone.rxStatus = '';
    clone.eta = '';
    clone.archived = false;
    clone.framesRemoved = [];
    clone.tax = 0;
    clone.shipping = 0;
    clone.total = 0;
    clone.vendorOrder = null;
    clone.fulfillment = 'PICKUP';
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.patient = context.patientId;
    clone.user = context.userId;
    clone.store = context.store;
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
      productElement.tax = 0;
      productElement.shipping = 0;
      productElement.destination = '';
      const retail = Number(productElement.retail) || 0;
      const custom = Number(productElement.custom) || 0;
      const lensPrice = Number(productElement.lens?.price) || 0;
      productElement.subtotal = retail + custom + lensPrice;

      if (!productElement.frame) {
        productElement.frame = {};
      }
      productElement.frame.pof = true;
      productElement.frame.deposit = 0;
      productElement.frame.enclosed = false;
      productElement.frame.physicalStatus = '';
      productElement.frame.preOrderStatus = '';
      productElement.frame.activity = [];
      productElement.frame.activityStatus = '';
      productElement.frame.dateReceived = '';
      productElement.frame.followUpDate = '';
      productElement.frame.followUpStatus = '';
      productElement.frame.referenceNumber = '';
      productElement.frame.labVendor = '';
      productElement.frame.carrier = '';
      productElement.frame.trackingNumber = '';
      productElement.frame.shipDate = '';
      productElement.frame.repairItems = [];
      productElement.frame.orderDate = '';
      productElement.frame.failedInspection = false;

      if (productElement.lens) {
        productElement.lens.labReferenceNumber = '';
        productElement.lens.POLLabReferenceNumber = '';
        productElement.lens.date = '';
        productElement.lens.failedInspection = '';
      }
    });

    const created = new LogElement();
    created.initials = context.initials;
    created.user = context.userId;
    created.item = 'JOB CREATED';
    created.notes = '';
    created.date = Date.now().toString();

    const redoLog = new LogElement();
    redoLog.initials = context.initials;
    redoLog.user = context.userId;
    redoLog.item = originalInvoice ? `RE-DO FROM ${originalInvoice}` : 'RE-DO';
    redoLog.notes = `REASON: ${reason}`;
    redoLog.date = Date.now().toString();
    clone.log = [created, redoLog];

    return clone as Order;
  }

  static create$(
    original: any,
    originalInvoice: string,
    existingOrders: Order[],
    reason: string,
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    },
    orderService: OrderService
  ): Observable<Order> {
    const invoiceNumber = RedoJob.nextRInvoice(originalInvoice, existingOrders);
    const newOrder = RedoJob.buildLinkedRedoOrder(
      original,
      originalInvoice,
      invoiceNumber,
      reason,
      context
    );
    return orderService.addOrder(newOrder).pipe(
      map((createdOrders: Order[]) =>
        Array.isArray(createdOrders) ? createdOrders[0] : createdOrders
      )
    );
  }
}
